import { useEffect, useState } from "react";
import { api } from "../api";
import { usd } from "../lib/money";
import { Money, MoneySwitch } from "./MoneyScope";

// Tax Desk — T1 of docs/TAX_DESK.md. Three things, in the order Alan asked
// for them: a point of view (what this desk assumes and why), considerations
// (what is time-boxed and about to expire), and a straight ledger of
// liabilities by treatment.
//
// Everything here is DIRECTIONAL and the page says so in three places rather
// than one, because a dollar figure on a screen reads as precise no matter
// what the footnote says. The broker documents are the tax record; this is
// what you know before they arrive.

type Bucket = {
  bucket: string; label: string; trades: number; realized: number;
  wash_disallowed: number; net_taxable: number; rate: number; rate_band: number[];
  estimated_tax: number;
};
type Wash = { symbol: string; book: string; loss: number; sold: string; repurchased: string;
  replacement: string; match: string; days: number; direction: string;
  still_deferred: boolean; note: string };
type Ledger = { year: number; money: string; books: string[]; buckets: Bucket[];
  estimated_tax: number; estimated_tax_band: number[]; wash_sales: Wash[];
  wash_detected: number; wash_still_deferred: number;
  open_lots: number; imprecision: string[]; authority: string };
type Consideration = { kind: string; severity: string; headline: string;
  authority: string; detail: string; days_left: number | null };
type Cliff = { symbol: string; sleeve: string; book: string; qty: number; cost_basis: number;
  opened: string; held_days: number; long_term_on: string; days_to_long_term: number };
type Harvest = { symbol: string; sleeve: string; book: string; tranche: string; qty: number;
  entry: number; mark: number | null; unrealized_loss: number; held_days: number | null;
  wash_sale_applies: boolean; blocked_symbols: string[]; thesis: string };
type Rate = { bucket: string; label: string; rate: number; band: number[]; made_of: string };
type Stance = { filing_status: string; income_assumption: string; rates: Rate[];
  caveats: string[]; authority: string; wash_window_days: number; cliff_warn_days: number };
type Overview = { as_of: string; money: string; filing_status: string; directional: boolean;
  ledger: Ledger; considerations: Consideration[]; cliffs: Cliff[]; harvest: Harvest[];
  treatment_note: string; paper_note?: string };

const pct = (r: number) => `${(r * 100).toFixed(1)}%`;
const SEV: Record<string, string> = {
  critical: "border-down/50 bg-down/10", warning: "border-gold/50 bg-gold/10",
  info: "border-slate-700 bg-slate-900/40",
};
const SEV_DOT: Record<string, string> = { critical: "bg-down", warning: "bg-gold", info: "bg-slate-500" };
const KIND_LABEL: Record<string, string> = {
  wash_window_open: "Wash-sale window open",
  holding_period_cliff: "Holding-period cliff",
  harvest_candidate: "Harvest candidate",
  collectibles_treatment: "Collectibles treatment",
};

export default function Tax() {
  const [money, setMoney] = useState<Money>("real");
  const [ov, setOv] = useState<Overview | null>(null);
  const [stance, setStance] = useState<Stance | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showStance, setShowStance] = useState(false);

  useEffect(() => {
    let live = true;
    setOv(null); setErr(null);
    api.get<Overview>(`/api/tax/overview?money=${money}`)
      .then((r) => { if (live) setOv(r); })
      .catch((e) => { if (live) setErr((e as Error).message); });
    return () => { live = false; };
  }, [money]);

  useEffect(() => {
    api.get<Stance>("/api/tax/stance").then(setStance).catch(() => {});
  }, []);

  if (err) return <div className="card text-down text-sm font-mono">tax desk unavailable — {err}</div>;

  const led = ov?.ledger;

  return (
    <div className="space-y-4">
      {/* ── Header + the directional warning, stated first ─────────────── */}
      <div className="card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="label mb-0">
              Tax Desk <span className="normal-case text-slate-500">— navigational awareness, not a tax return</span>
            </h2>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              Every figure below is <span className="text-gold font-semibold">directional</span>. The
              rates are assumptions for someone earning {stance?.income_assumption ?? "$300–400k"}, not a
              computation of your liability. The broker documents — 1099-B, 1099-DA, OANDA's annual
              statement — are the tax record.
            </p>
          </div>
          <MoneySwitch book="all" money={money} onChange={setMoney} />
        </div>
        {ov?.paper_note && (
          <p className="mt-3 text-xs text-accent border border-accent/30 bg-accent/5 rounded p-2">{ov.paper_note}</p>
        )}
      </div>

      {!ov && <div className="card text-sm text-slate-500 font-mono">loading tax view…</div>}

      {ov && led && (
        <>
          {/* ── The headline estimate, as a band ─────────────────────────── */}
          <div className="card">
            <div className="flex items-end gap-6 flex-wrap">
              <div>
                <div className="label">Estimated {led.year} liability</div>
                <div className="text-3xl font-mono font-semibold tabular-nums">
                  {usd(led.estimated_tax)}
                </div>
                <div className="text-xs text-slate-500 font-mono mt-1">
                  band {usd(led.estimated_tax_band[0])} – {usd(led.estimated_tax_band[1])}
                </div>
              </div>
              <div className="flex-1 min-w-[16rem]">
                <div className="text-xs text-slate-400 leading-relaxed">
                  {ov.filing_status === "unknown" ? (
                    <>
                      The band is <span className="text-slate-200">filing status</span>, which the firm
                      does not know. A single filer at this income sits in the 35% bracket; married
                      filing jointly is mostly 24% — about a quarter of the estimate. Set{" "}
                      <code className="text-accent">tax.filing_status</code> in Config Studio to
                      narrow it.
                    </>
                  ) : (
                    <>
                      Filing <span className="text-slate-200">{ov.filing_status.toUpperCase()}</span>.
                      The band is now only <span className="text-slate-200">where in the income
                      range</span> you land — the 24% bracket runs to roughly $394k of taxable income,
                      so the top of the range can touch 32%. The assumed rate is the floor.
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Considerations: what is time-boxed ───────────────────────── */}
          <div className="card">
            <h3 className="label">Considerations</h3>
            {ov.considerations.length === 0 ? (
              <p className="text-sm text-slate-500">
                Nothing time-boxed today — no open wash-sale windows, no holding-period cliffs inside
                {" "}{stance?.cliff_warn_days ?? 90} days, no unrealized losses to harvest.
              </p>
            ) : (
              <ul className="space-y-2 mt-2">
                {ov.considerations.map((c, i) => (
                  <li key={i} className={`rounded border p-3 ${SEV[c.severity] ?? SEV.info}`}>
                    <div className="flex items-start gap-2">
                      <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${SEV_DOT[c.severity] ?? SEV_DOT.info}`} />
                      <div className="min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-wider text-slate-500">
                            {KIND_LABEL[c.kind] ?? c.kind}
                          </span>
                          {c.days_left !== null && (
                            <span className="text-[10px] font-mono text-slate-400">{c.days_left}d</span>
                          )}
                        </div>
                        <div className="text-sm text-slate-100">{c.headline}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{c.detail}</div>
                        <div className="text-[10px] text-slate-600 mt-1 font-mono">{c.authority}</div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── The ledger ───────────────────────────────────────────────── */}
          <div className="card overflow-x-auto">
            <h3 className="label">Liability ledger — realized {led.year}, by treatment</h3>
            <p className="text-xs text-slate-500 mb-2">{ov.treatment_note}</p>
            <table className="w-full text-sm font-mono tabular-nums">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 text-right">
                  <th className="text-left font-normal py-1">Treatment</th>
                  <th className="font-normal">Trades</th>
                  <th className="font-normal">Realized</th>
                  <th className="font-normal">Wash add-back</th>
                  <th className="font-normal">Net taxable</th>
                  <th className="font-normal">Assumed rate</th>
                  <th className="font-normal">Est. tax</th>
                </tr>
              </thead>
              <tbody>
                {led.buckets.map((b) => (
                  <tr key={b.bucket} className="border-t border-slate-800 text-right">
                    <td className="text-left py-1.5 text-slate-200 font-sans">{b.label}</td>
                    <td className="text-slate-400">{b.trades}</td>
                    <td className={b.realized >= 0 ? "text-up" : "text-down"}>{usd(b.realized)}</td>
                    <td className={b.wash_disallowed ? "text-gold" : "text-slate-600"}>
                      {b.wash_disallowed ? usd(b.wash_disallowed) : "—"}
                    </td>
                    <td className={b.net_taxable >= 0 ? "text-slate-100" : "text-down"}>{usd(b.net_taxable)}</td>
                    <td className="text-slate-400">
                      {pct(b.rate)}
                      <span className="text-slate-600 text-xs"> ({pct(b.rate_band[0])}–{pct(b.rate_band[1])})</span>
                    </td>
                    <td className="text-slate-100">{usd(b.estimated_tax)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-slate-700 text-right font-semibold">
                  <td className="text-left py-1.5 font-sans">Total (estimated)</td>
                  <td colSpan={5} />
                  <td>{usd(led.estimated_tax)}</td>
                </tr>
              </tbody>
            </table>
            <ul className="mt-3 space-y-1">
              {led.imprecision.map((s, i) => (
                <li key={i} className="text-[11px] text-slate-500 flex gap-1.5">
                  <span className="text-slate-700">·</span><span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Wash sales already taken ─────────────────────────────────── */}
          {led.wash_sales.length > 0 && (
            <div className="card overflow-x-auto">
              <h3 className="label">
                Wash sales — {led.wash_detected} detected,{" "}
                <span className={led.wash_still_deferred ? "text-gold" : "text-slate-400"}>
                  {led.wash_still_deferred} still deferred
                </span>
              </h3>
              <p className="text-xs text-slate-500 mb-2">
                IRC §1091: the loss is disallowed and its basis rolls into the replacement lot — deferred,
                not destroyed. Closing that replacement in the same tax year recovers the deduction, which
                is what usually happens to a book that goes flat by close. <span className="text-slate-400">
                Only the still-deferred ones are added back above.</span> Applies to equities and options;
                crypto is property and is not covered today.
              </p>
              <table className="w-full text-sm font-mono tabular-nums">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 text-right">
                    <th className="text-left font-normal py-1">Symbol</th>
                    <th className="font-normal">Loss</th>
                    <th className="font-normal">Sold</th>
                    <th className="font-normal">Repurchased</th>
                    <th className="text-left font-normal pl-4">Match</th>
                    <th className="text-left font-normal pl-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {led.wash_sales.map((w, i) => (
                    <tr key={i} className={`border-t border-slate-800 text-right ${w.still_deferred ? "" : "opacity-60"}`}>
                      <td className="text-left py-1.5 text-slate-200">{w.symbol}
                        <span className="text-slate-600 text-xs"> · {w.book}</span></td>
                      <td className="text-down">{usd(w.loss)}</td>
                      <td className="text-slate-400">{w.sold}</td>
                      <td className="text-slate-400">{w.repurchased}</td>
                      <td className="text-left pl-4 text-slate-400">
                        {w.replacement}{" "}
                        <span className={w.match === "identical" ? "text-slate-600" : "text-gold"}>
                          ({w.match}, {Math.abs(w.days)}d {w.direction})
                        </span>
                      </td>
                      <td className="text-left pl-4 font-sans text-xs">
                        <span className={w.still_deferred ? "text-gold" : "text-slate-500"}>
                          {w.still_deferred ? "deferred past year end" : "recovered same year"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Holding-period cliffs ────────────────────────────────────── */}
          {ov.cliffs.length > 0 && (
            <div className="card overflow-x-auto">
              <h3 className="label">Holding-period cliffs</h3>
              <p className="text-xs text-slate-500 mb-2">
                When each open lot crosses into long-term treatment (§1222, more than one year).
                The daily book never appears here — flat-by-close makes it short-term by construction.
              </p>
              <table className="w-full text-sm font-mono tabular-nums">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 text-right">
                    <th className="text-left font-normal py-1">Symbol</th>
                    <th className="font-normal">Held</th>
                    <th className="font-normal">Long-term on</th>
                    <th className="font-normal">Days away</th>
                  </tr>
                </thead>
                <tbody>
                  {ov.cliffs.map((c, i) => (
                    <tr key={i} className="border-t border-slate-800 text-right">
                      <td className="text-left py-1.5 text-slate-200">{c.symbol}
                        <span className="text-slate-600 text-xs"> · {c.book}</span></td>
                      <td className="text-slate-400">{c.held_days}d</td>
                      <td className="text-slate-400">{c.long_term_on}</td>
                      <td className={c.days_to_long_term <= 30 ? "text-gold" : "text-slate-400"}>
                        {c.days_to_long_term}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Harvest candidates ───────────────────────────────────────── */}
          {ov.harvest.length > 0 && (
            <div className="card">
              <h3 className="label">Harvest candidates</h3>
              <p className="text-xs text-slate-500 mb-2">
                Surfaced only — nothing here is ever executed automatically. A loss harvested out of a
                position the board still believes in is usually a bad trade wearing a tax costume, so
                each row carries the thesis it would abandon.
              </p>
              <ul className="space-y-2">
                {ov.harvest.map((h, i) => (
                  <li key={i} className="rounded border border-slate-800 p-3">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <span className="text-slate-200 font-mono">{h.symbol}
                        <span className="text-slate-600 text-xs"> · {h.sleeve} · {h.book}</span></span>
                      <span className="font-mono tabular-nums text-down">{usd(h.unrealized_loss)}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      {h.wash_sale_applies ? (
                        <>Selling opens a 30-day block on{" "}
                          <span className="font-mono text-gold">{h.blocked_symbols.slice(0, 5).join(", ")}</span>.</>
                      ) : (
                        <>Crypto is property — §1091 does not reach it today, so a repurchase is not blocked.</>
                      )}
                    </div>
                    {h.thesis && <div className="text-xs text-slate-500 mt-1 italic">Thesis: {h.thesis}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ── Point of view ────────────────────────────────────────────── */}
          {stance && (
            <div className="card">
              <button className="label mb-0 flex items-center gap-2" onClick={() => setShowStance((s) => !s)}>
                Point of view <span className="text-slate-600">{showStance ? "▾" : "▸"}</span>
              </button>
              {showStance && (
                <div className="mt-3 space-y-3">
                  <table className="w-full text-sm font-mono tabular-nums">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                        <th className="text-left font-normal py-1">Treatment</th>
                        <th className="text-right font-normal">Assumed</th>
                        <th className="text-left font-normal pl-4">Made of</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stance.rates.map((r) => (
                        <tr key={r.bucket} className="border-t border-slate-800">
                          <td className="py-1.5 text-slate-200 font-sans">{r.label}</td>
                          <td className="text-right text-slate-100">{pct(r.rate)}</td>
                          <td className="pl-4 text-slate-500 text-xs font-sans">{r.made_of}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <ul className="space-y-1">
                    {stance.caveats.map((c, i) => (
                      <li key={i} className="text-xs text-slate-400 flex gap-1.5">
                        <span className="text-slate-700">·</span><span>{c}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-slate-500 border-t border-slate-800 pt-2">{stance.authority}</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
